//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import { useSelector } from 'react-redux';

import { useSocialNetworkUserInfoQuery } from '@account-settings/queries/socialNetworksUserInfoQueries';
import {
  CuralateFacebookFeature,
  CuralateInstagramFeature,
} from '@account-settings/redux/facebook-instagram/curalateFbIgFeatureInfoModel';
import { getFacebookConnectionData } from '@account-settings/redux/facebook-instagram/selectors/getFacebookConnectionData';
import {
  getClientFacebookFeatures,
  getClientInstagramFeatures,
} from '@account-settings/redux/facebook-instagram/selectors/getFbIgFeatures';
import { useConnectedInstagramAccount } from '@instagram-account/hooks/useConnectedInstagramAccount';
import IFacebookPageToken from '@models/IFacebookPageToken';
import { SocialNetwork } from '@models/SocialNetwork';

/**
 * Asynchronously fetches Facebook Features Data if necessary and returns it.
 * @returns undefined if data is not ready yet, connectedFacebookPageToken if it is available
 */
export const useConnectedFacebookPage = (): IFacebookPageToken | undefined => {
  const data = useSelector(getFacebookConnectionData);

  useSocialNetworkUserInfoQuery(SocialNetwork.Facebook);

  if (
    !data.isFetchingConnectedFacebookPageToken &&
    !data.isFetchingFacebookUserToken
  ) {
    return data.connectedFacebookPageToken;
  }
};

/**
 * Checks whether the user has a given instagram feature
 * @param feature The instagram feature to be checked
 * @returns undefined if data is not ready yet, true if feature is present, false if not
 */
export const useGetInstagramFeature = (
  feature: CuralateInstagramFeature,
): boolean | undefined => {
  const { connectedInstagramAccount: featuresData } =
    useConnectedInstagramAccount();
  const instagramFeatures = useSelector(getClientInstagramFeatures);
  const { isFetchingConnectedInstagramAccount, isFetchingFacebookUserToken } =
    useSelector((state) => state.facebookInstagramSettings.flags);
  if (featuresData) {
    return instagramFeatures[feature];
  }
  if (!isFetchingConnectedInstagramAccount && !isFetchingFacebookUserToken) {
    return false;
  }
};

/**
 * Checks whether the user has a given facebook feature
 * @param feature The facebook feature to be checked
 * @returns undefined if data is not ready yet, true if feature is present, false if not
 */
export const useGetFacebookFeature = (
  feature: CuralateFacebookFeature,
): boolean | undefined => {
  const featuresData = useConnectedFacebookPage();
  const facebookFeatures = useSelector(getClientFacebookFeatures);
  const { isFetchingConnectedFacebookPageToken, isFetchingFacebookUserToken } =
    useSelector((state) => state.facebookInstagramSettings.flags);
  if (featuresData) {
    return facebookFeatures[feature];
  }
  if (!isFetchingConnectedFacebookPageToken && !isFetchingFacebookUserToken) {
    return false;
  }
};
